import HorizontalDivider from '@/global/components/Divider/HorizontalDivider'
import { Link } from '@/global/components/Link'
import { hasPermission } from '@/hooks/useBackendMenu'
import { cn } from '@/lib/utils'
import { CategoriaEnum } from '@acme/auth'
import dayjs from 'dayjs'
import { Button } from 'global/components/Button'
import IconButton from 'global/components/Button/IconButton'
import { TextField } from 'global/components/TextField'
import {
    Popover,
    PopoverAnchor,
    PopoverContent,
    PopoverTrigger,
} from 'global/components/ui/popover'
import { useAuth } from 'hooks'
import { EyeClosedIcon, EyeIcon, XIcon } from 'lucide-react'
import { type SyntheticEvent, useId, useRef, useState } from 'react'
import { type ErrorHandler, type NotaAdesiva, notasAdesivas } from 'service/api'
import { toast } from 'sonner'
import type { AncoraNota } from './ancora'
import { NotaAdesivaIconButton } from './nota-adesiva-icon'

const TEXTO_MAX_CARACTERES = 500

export interface NotasAdesivasPopoverProps {
    /** Cabeçalho já montado pelo chamador, ex.: "SEG 03/08 | 1ª entrada 07:30". */
    titulo: string
    /** Âncora onde a nova nota é criada. */
    ancora: AncoraNota
    /** Notas já filtradas para esta âncora. */
    notas: NotaAdesiva[]
    /** Classe do botão-ícone que abre o popover. */
    className?: string
    variant?: 'dynamic' | 'static'
    /**
     * Dentro de diálogo modal: o bloqueio de scroll do diálogo descarta a roda
     * do mouse fora dele, e o conteúdo do popover é portado para o `body`.
     * Em modo modal o popover assume o bloqueio e rola a própria lista.
     */
    isModal?: boolean
}

/**
 * Eventos do portal sobem pela árvore React até a célula da grade, que
 * navega por Tab, abre o registro no clique e seleciona a linha na visão diária.
 */
function pararPropagacao(event: SyntheticEvent) {
    event.stopPropagation()
}

function mostrarErro(erro: ErrorHandler) {
    toast.error([erro.message].flat().join(', '))
}

/** Ícone de notas de uma âncora da Digitação que abre a lista e o formulário de nova nota. */
export function NotasAdesivasPopover({
    titulo,
    ancora,
    notas = [],
    className,
    variant = 'dynamic',
    isModal = false,
}: NotasAdesivasPopoverProps) {
    const [isAberto, setIsAberto] = useState(false)
    const idTitulo = useId()
    const refIcone = useRef<HTMLButtonElement>(null)
    const refRetanguloAbertura = useRef(new DOMRect())
    // Objeto estável: o Radix guarda a âncora virtual da primeira renderização.
    const refAncoraFixa = useRef({
        getBoundingClientRect: () => refRetanguloAbertura.current,
    })

    /**
     * Congela a posição do ícone na abertura: ancorado ao ícone vivo, o popover
     * acompanhava o hover que o amplia e a rolagem da grade que o tira da tela.
     */
    function alterarAbertura(isAbrindo: boolean) {
        const retangulo = refIcone.current?.getBoundingClientRect()
        if (isAbrindo && retangulo) refRetanguloAbertura.current = retangulo
        setIsAberto(isAbrindo)
    }

    return (
        <Popover open={isAberto} onOpenChange={alterarAbertura} modal={isModal}>
            <PopoverTrigger asChild>
                <NotaAdesivaIconButton
                    ref={refIcone}
                    notas={notas}
                    className={cn(className, {
                        'size-10 [&_svg]:size-7 opacity-100 scale-100!':
                            variant == 'static',
                    })}
                    tabIndex={-1}
                    onClick={pararPropagacao}
                    onKeyDown={pararPropagacao}
                />
            </PopoverTrigger>
            <PopoverAnchor virtualRef={refAncoraFixa} />
            <PopoverContent
                arrow={false}
                align="start"
                alignOffset={35}
                sideOffset={-35}
                collisionPadding={8}
                aria-labelledby={idTitulo}
                className="flex max-h-(--radix-popover-content-available-height) w-96 flex-col p-0"
                onClick={pararPropagacao}
                onKeyDown={pararPropagacao}
            >
                <header className="flex shrink-0 items-center justify-between border-b px-3 py-1.5">
                    <h3 id={idTitulo} className="font-semibold">
                        {titulo}
                    </h3>
                    <IconButton
                        aria-label="Fechar"
                        onClick={() => setIsAberto(false)}
                        title="Fechar"
                    >
                        <XIcon />
                    </IconButton>
                </header>
                <ul className="flex min-h-0 flex-col gap-2 overflow-y-auto border-b p-1">
                    {notas.map(nota => (
                        <NotaCard key={nota.id} nota={nota} />
                    ))}
                    {hasPermission(['create', 'NotasAdesivas']) && (
                        <NovaNota ancora={ancora} />
                    )}
                </ul>
            </PopoverContent>
        </Popover>
    )
}

function NotaCard({ nota }: { nota: NotaAdesiva }) {
    const [textoEdicao, setTextoEdicao] = useState<string | null>(null)
    const { mutate: atualizar, isLoading: isAtualizando } =
        notasAdesivas.mutations.useUpdate({
            invalidateQuery: true,
            onError: mostrarErro,
        })
    const { mutate: excluir, isLoading: isExcluindo } =
        notasAdesivas.mutations.useDelete({
            invalidateQuery: true,
            onError: erro => mostrarErro(erro as ErrorHandler),
        })

    const isEditando = textoEdicao !== null
    const canAtualizar = hasPermission(['update', 'NotasAdesivas'])
    const canEditarTexto = nota.canEditarTexto && canAtualizar
    const canAlternarVisibilidade = nota.canAlternarVisibilidade && canAtualizar
    const canExcluir =
        nota.canExcluir && hasPermission(['delete', 'NotasAdesivas'])

    const salvarTexto = () => {
        if (!textoEdicao?.trim()) return
        atualizar(
            { id: nota.id, texto: textoEdicao },
            { onSuccess: () => setTextoEdicao(null) },
        )
    }

    return (
        <li className="flex flex-col gap-1 rounded-md border p-2">
            <div className="flex items-baseline gap-2 text-sm">
                <span
                    className="font-semibold truncate max-w-1/2"
                    title={nota.autor.nome}
                >
                    {nota.autor.nome}
                </span>
                <div className="flex-1" />
                <span className="text-muted-foreground text-xs text-nowrap">
                    {dayjs(nota.createdAt).format('DD/MMM HH:mm')}
                </span>
                {nota.updatedAt !== nota.createdAt && (
                    <span
                        className="italic text-xs text-muted-foreground"
                        title={
                            'últ. edição em ' +
                            dayjs(nota.updatedAt).format('DD/MMM HH:mm')
                        }
                    >
                        Editado
                    </span>
                )}
            </div>
            {isEditando ? (
                <div className="flex flex-col gap-2">
                    <TextField
                        multiline
                        rows={2}
                        unHelperText
                        variant="outlined"
                        aria-label="Texto da nota"
                        maxLength={TEXTO_MAX_CARACTERES}
                        value={textoEdicao}
                        onChange={event => setTextoEdicao(event.target.value)}
                    />
                    <div className="flex justify-end gap-2">
                        <Button
                            variant="text"
                            size="small"
                            onClick={() => setTextoEdicao(null)}
                        >
                            Cancelar
                        </Button>
                        <Button
                            size="small"
                            disabled={!textoEdicao.trim() || isAtualizando}
                            onClick={salvarTexto}
                        >
                            Salvar
                        </Button>
                    </div>
                </div>
            ) : (
                <p className="whitespace-pre-wrap break-words">{nota.texto}</p>
            )}
            <div className="flex items-center justify-between gap-2 border-t pt-2 text-sm">
                {canAlternarVisibilidade ? (
                    <AlternarVisibilidade
                        visivel={nota.visivelFuncionario}
                        disabled={isAtualizando}
                        onChange={value =>
                            atualizar({
                                id: nota.id,
                                visivelFuncionario: value,
                            })
                        }
                    />
                ) : (
                    <span />
                )}
                <div className="flex items-center gap-3">
                    {canEditarTexto && !isEditando && (
                        <Link onClick={() => setTextoEdicao(nota.texto)}>
                            Editar
                        </Link>
                    )}
                    {canExcluir && (
                        <Link
                            aria-disabled={isExcluindo}
                            className={cn('text-error', {
                                'pointer-events-none opacity-disabled':
                                    isExcluindo,
                            })}
                            onClick={() => excluir(nota.id)}
                        >
                            Excluir
                        </Link>
                    )}
                </div>
            </div>
        </li>
    )
}

interface IAlternarProps {
    visivel: boolean
    disabled?: boolean
    onChange: (value: boolean) => void
}
function AlternarVisibilidade({ onChange, visivel, disabled }: IAlternarProps) {
    const [visivelAnterior, setVisivelAnterior] = useState(visivel)
    const [totalTrocas, setTotalTrocas] = useState(0)

    // Compara com o valor anterior em vez de marcar no clique: na nota salva o
    // prop só muda após a resposta da API, e o aviso não deve mostrar o estado velho.
    if (visivel !== visivelAnterior) {
        setVisivelAnterior(visivel)
        setTotalTrocas(total => total + 1)
    }

    return (
        <div className="flex items-center gap-1 overflow-hidden">
            <IconButton
                size="small"
                className="relative transition-[opacity,colors] duration-200"
                onClick={() => onChange(!visivel)}
                disabled={disabled}
                title={
                    visivel
                        ? 'Ocultar para o funcionário'
                        : 'Tornar visível para o funcionário'
                }
            >
                <EyeIcon
                    className={cn('text-success', { 'opacity-0': !visivel })}
                />
                <EyeClosedIcon
                    className={cn('absolute text-primary', {
                        'opacity-0': visivel,
                    })}
                />
            </IconButton>
            {totalTrocas > 0 && (
                <span
                    // `key` remonta o aviso e reinicia a animação a cada troca.
                    key={totalTrocas}
                    role="status"
                    className="grid grid-cols-[0fr] animate-aviso-lateral"
                >
                    <span className="min-w-0 overflow-hidden">
                        <span
                            className={cn(
                                'whitespace-nowrap text-xs',
                                visivel
                                    ? 'text-success'
                                    : 'text-muted-foreground',
                            )}
                        >
                            {visivel
                                ? 'Visível para o funcionário'
                                : 'Oculto para o funcionário'}
                        </span>
                    </span>
                </span>
            )}
        </div>
    )
}

function NovaNota({ ancora }: { ancora: AncoraNota }) {
    const {
        decoded: { categoria },
    } = useAuth()
    const isGestor = categoria !== CategoriaEnum.Funcionario
    const [texto, setTexto] = useState('')
    const [isVisivelFuncionario, setIsVisivelFuncionario] = useState(false)
    const { mutate: criar, isLoading } = notasAdesivas.mutations.useCreate({
        invalidateQuery: true,
        onError: mostrarErro,
    })

    const salvar = () => {
        const alvo =
            'registroId' in ancora
                ? { registroId: ancora.registroId }
                : {
                      funcionarioId: ancora.funcionarioId,
                      data: ancora.data.slice(0, 10),
                  }

        criar(
            {
                ...alvo,
                texto,
                ...(isGestor && { visivelFuncionario: isVisivelFuncionario }),
            },
            {
                onSuccess: () => {
                    setTexto('')
                    setIsVisivelFuncionario(false)
                },
            },
        )
    }

    return (
        <div className="flex shrink-0 flex-col gap-2 pb-1.5 px-1">
            <HorizontalDivider />
            <TextField
                multiline
                rows={2}
                unHelperText
                variant="outlined"
                aria-label="Nova nota"
                placeholder="Escrever nova observação…"
                maxLength={TEXTO_MAX_CARACTERES}
                value={texto}
                onChange={event => setTexto(event.target.value)}
            />
            <div className="flex items-center justify-between gap-1 text-sm">
                {isGestor ? (
                    <AlternarVisibilidade
                        visivel={isVisivelFuncionario}
                        onChange={value => setIsVisivelFuncionario(value)}
                    />
                ) : (
                    <span />
                )}
                <Button
                    size="small"
                    disabled={!texto.trim() || isLoading}
                    onClick={salvar}
                >
                    Publicar
                </Button>
            </div>
        </div>
    )
}
